import { getTaskOutcome } from "../api/getTaskOutcome";
import { getTaskReviewed, type TaskReviewedMarks } from "../api/getTaskReviewed";
import type { TaskOutcome } from "../TaskOutcome";

/**
 * 結果情報と確認済みを保持する。
 *
 * 生成結果の画面は 2 つの器を同時に要る。**1 回の照会でまとめて取る**のは、別々に取ると開くたび
 * に待ちが積み上がるためで、既存の結果の器と同じ作りにしてある
 * （[[2026-09-10 設計ノート アドイン 生成結果画面の結果情報対応]]）。
 *
 * 確認済みだけは画面から書き換わるので、保存が通ったら手元も差し替える。
 */

type OutcomeEntry = {
    status: "loading" | "loaded" | "error";
    /** `null` は「この器より前に完了した過去のタスク」。画面は現行の一覧へ落ちる。 */
    outcome: TaskOutcome | null;
    reviewed: TaskReviewedMarks | null;
};

const cache = new Map<string, OutcomeEntry>();
const listeners = new Set<() => void>();
const inflight = new Set<string>();

function emit(): void {
    listeners.forEach((l) => l());
}

function cacheKey(taskId: string, projectId: string): string {
    return `${projectId}::${taskId}`;
}

export function getOutcomeEntry(
    taskId: string | null,
    projectId: string | null
): OutcomeEntry | null {
    if (!taskId || !projectId) return null;
    const key = cacheKey(taskId, projectId);
    const existing = cache.get(key);
    if (existing) return existing;

    if (!inflight.has(key)) {
        inflight.add(key);
        cache.set(key, { status: "loading", outcome: null, reviewed: null });
        // **確認済みの失敗で結果まで落とさない。** 記録が取れなくても中身は読めるので、
        // 読めるものまで読めなくするほうが損である。
        Promise.all([
            getTaskOutcome(projectId, taskId),
            getTaskReviewed(projectId, taskId).catch(() => null),
        ]).then(
            ([outcome, reviewed]) => {
                cache.set(key, { status: "loaded", outcome, reviewed });
                inflight.delete(key);
                emit();
            },
            () => {
                cache.set(key, { status: "error", outcome: null, reviewed: null });
                inflight.delete(key);
                emit();
            }
        );
    }

    return cache.get(key) ?? null;
}

export function subscribeOutcome(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

/** 保存が通った確認済みを手元へ映す。**取り直さない**（応答が最新なので照会は無駄になる）。 */
export function setReviewed(taskId: string, projectId: string, reviewed: TaskReviewedMarks): void {
    const key = cacheKey(taskId, projectId);
    const entry = cache.get(key);
    if (!entry) return;
    cache.set(key, { ...entry, reviewed });
    emit();
}

/** 取ったものを捨てて、次の照会でサーバーへ行かせる。 */
export function invalidateOutcome(taskId: string | null, projectId: string | null): void {
    if (!taskId || !projectId) return;
    cache.delete(cacheKey(taskId, projectId));
    emit();
}

export type { OutcomeEntry };
